import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import type { Color } from "../../lib/colors";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

export const textVariants = cva("", {
  variants: {
    size: {
      // DESIGN-TUNABLE: default is the base control idiom (16px mobile,
      // 14px ≥sm). It lives INSIDE the variant so explicit sizes replace the
      // responsive pair instead of fighting it in tw-merge.
      default: "text-base sm:text-sm",
      "2xs": "text-2xs", xs: "text-xs", sm: "text-sm", base: "text-base",
      lg: "text-lg", xl: "text-xl", "2xl": "text-2xl",
      "3xl": "text-3xl", "4xl": "text-4xl",
    },
    weight: {
      normal: "font-normal", medium: "font-medium",
      semibold: "font-semibold", bold: "font-bold",
    },
    variant: {
      default: "",
      muted: "text-muted-foreground",
      subtle: "text-muted-foreground/72",
      primary: "text-primary",
      destructive: "text-destructive-foreground",
      success: "text-success-foreground",
      warning: "text-warning-foreground",
      info: "text-info-foreground",
      // Inline link inside running text — the anchor idiom Button
      // variant="link" gives standalone actions. Pair with `as="a"`.
      link: "text-primary underline-offset-4 hover:underline",
    },
    align: { left: "text-left", center: "text-center", right: "text-right" },
    mono: { true: "font-mono" },
    // Preformatted: newlines and indentation render as written, long lines
    // wrap instead of forcing horizontal scroll — code/JSON previews.
    pre: { true: "whitespace-pre-wrap break-words" },
    italic: { true: "italic" },
    strike: { true: "line-through" },
    // Fixed-width digits — metrics/counters that must not jitter as they tick.
    tabular: { true: "tabular-nums" },
    // "none" pins the line box to glyph height — chip labels use it so
    // ghost-wash bleeds land predictably and chip height matches the text.
    leading: {
      none: "leading-none",
      tight: "leading-tight",
      normal: "leading-normal",
    },
  },
  defaultVariants: { size: "default", variant: "default" },
});

export type TextElement =
  | "span" | "p" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6"
  | "label" | "code" | "blockquote" | "a";

const TRUNCATE: Record<number, string> = {
  1: "line-clamp-1", 2: "line-clamp-2", 3: "line-clamp-3",
  4: "line-clamp-4", 5: "line-clamp-5", 6: "line-clamp-6",
};

interface TextOwnProps extends VariantProps<typeof textVariants> {
  truncate?: boolean | keyof typeof TRUNCATE;
  /**
   * A palette or semantic color NAME. Emits `data-color` and `text-c`, which
   * `tokens.css` binds to the hue in both modes. Wins over `variant`'s color.
   */
  color?: Color;
}

// Generic over the rendered element — `as` IS the type parameter, so
// `as="a"` carries href/target/rel and `as="label"` carries htmlFor.
export type TextProps<T extends TextElement = "span"> = Omittable<
  TextOwnProps & { as?: T }
> &
  Omit<useRender.ComponentProps<T>, keyof TextOwnProps | "as">;

export function Text<T extends TextElement = "span">({
  as, render, className, size, weight, variant, align, mono, pre, italic,
  strike, tabular, leading, truncate, color, ...props
}: TextProps<T>): React.ReactElement {
  const defaultProps = {
    className: cn(
      textVariants({
        size, weight, variant, align, mono, pre, italic, strike, tabular,
        leading,
      }),
      truncate === true && "truncate",
      typeof truncate === "number" && TRUNCATE[truncate],
      color && "text-c",
      className,
    ),
    "data-color": color,
    "data-slot": "text",
  };
  return useRender({
    defaultTagName: as ?? "span",
    props: mergeProps<"span">(
      defaultProps,
      props as useRender.ComponentProps<"span">,
    ),
    render,
  });
}
